import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Menu, X, Instagram, Heart, Gift, Star, 
  ArrowRight, CheckCircle2
} from 'lucide-react';
import Official from '../../components/Official';
import Trust from '../../components/Trust';
import Offer from '../../components/Offer';
import Footer from '../../components/Footer';

const CAMPAIGN_SOURCE = 'para-mama';

export const ParaMama = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('campaign_source', CAMPAIGN_SOURCE);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const reasons = [
    { icon: Star, title: "Útil", desc: "Las usará en el día a día, no solo el domingo." },
    { icon: Gift, title: "Original", desc: "Se sale de las típicas billeteras, perfumes o corbatas." },
    { icon: CheckCircle2, title: "Presentación premium", desc: "Caja y funda listas para entregar sin envolver nada más." },
    { icon: Heart, title: "Con identidad", desc: "Muestra su pasión por La Tri de forma elegante." }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-rose-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_13e5738d-7aa3-4072-b101-9672dafbd61a/artifacts/j65cu3zm_logo.jpg" 
                alt="Gafas La Tri - Logo Oficial" 
                className="h-14 w-auto object-contain"
                loading="eager"
                fetchpriority="high"
                width="120"
                height="56"
              />
            </Link>

            <nav className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('comprar')} className="text-gray-700 hover:text-rose-500 font-medium transition-colors" data-testid="nav-comprar">Comprar</button>
              <button onClick={() => scrollToSection('regalos')} className="text-gray-700 hover:text-rose-500 font-medium transition-colors" data-testid="nav-regalos">Regalos</button>
              <button onClick={() => scrollToSection('entrega')} className="text-gray-700 hover:text-rose-500 font-medium transition-colors" data-testid="nav-entrega">Entrega</button>
              <a
                href="https://www.instagram.com/gafasdelatri"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-rose-500 font-medium transition-colors flex items-center gap-2"
                data-testid="nav-instagram"
              >
                <Instagram size={18} />
              </a>
              <button 
                onClick={() => scrollToSection('comprar')} 
                data-testid="header-cta-comprar"
                className="bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-rose-200 hover:scale-105 transition-all flex items-center gap-2"
              >
                <Gift size={18} />
                Comprar regalo
              </button>
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-rose-100 bg-white">
              <nav className="flex flex-col space-y-3">
                <button onClick={() => scrollToSection('comprar')} className="text-left text-gray-700 font-medium py-2">Comprar</button>
                <button onClick={() => scrollToSection('regalos')} className="text-left text-gray-700 font-medium py-2">Regalos</button>
                <button onClick={() => scrollToSection('entrega')} className="text-left text-gray-700 font-medium py-2">Entrega</button>
                <button onClick={() => scrollToSection('comprar')} className="bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold text-left">
                  Comprar regalo
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      <main className="pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-rose-50 via-pink-50 to-white">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 lg:px-8 py-20 lg:py-28 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white border border-rose-200 text-rose-600 px-5 py-2 rounded-full text-sm font-bold mb-8 shadow-sm">
                  <Heart size={16} fill="currentColor" />
                  REGALO DEL DÍA DEL PADRE
                </div>
                
                <h1 className="text-5xl lg:text-7xl font-black text-gray-900 mb-6 leading-[1.05] tracking-tight">
                  Un regalo
                  <span className="block italic font-light text-rose-500">diferente</span>
                  para el Día del Padre
                </h1>
                
                <p className="text-lg lg:text-xl text-gray-600 mb-10 leading-relaxed max-w-xl font-light">
                  Sorpréndelo con unas gafas oficiales de La Tri: útiles, originales y con una presentación que lo va a dejar sin palabras.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => scrollToSection('comprar')}
                    data-testid="hero-cta-elegir"
                    className="group bg-gradient-to-r from-rose-500 to-pink-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-rose-200 hover:scale-105 transition-all inline-flex items-center justify-center gap-2"
                  >
                    Elegir regalo
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button 
                    onClick={() => scrollToSection('entrega')}
                    data-testid="hero-cta-ver-producto"
                    className="border-2 border-rose-300 text-rose-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-rose-50 transition-all inline-flex items-center justify-center gap-2"
                  >
                    Ver cómo llega el producto
                  </button>
                </div>

                <div className="flex items-center gap-6 mt-10 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Star className="text-yellow-500" size={18} fill="currentColor" />
                    <span className="font-semibold">Producto Oficial</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="text-green-500" size={18} />
                    <span className="font-semibold">Envío gratis sobre $50</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-rose-200 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-pink-200 rounded-full blur-2xl"></div>
                <img 
                  src="/lifestyle/regalo_caja_lifestyle.png"
                  alt="Caja de regalo con Gafas La Tri y funda oficial"
                  className="relative rounded-3xl shadow-2xl w-full object-cover aspect-square"
                  loading="eager"
                  fetchpriority="high"
                />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-full shadow-xl px-6 py-3 flex items-center gap-2 border border-rose-100">
                  <Gift className="text-rose-500" size={20} />
                  <span className="font-bold text-gray-900 text-sm">Listo para regalar</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* OFFICIAL - Producto Oficial con Licencia */}
        <Official />

        {/* OFFER - Merged section: color selection + pack offer */}
        <Offer
          colorHeading="Escoge el color que más va con él"
          headingTitle="Una para ti,"
          headingTitleAccent="otra para compartir"
          headingSubtitle="Aprovecha el precio de lanzamiento y llévate dos pares para regalar y compartir."
        />

        {/* POR QUÉ FUNCIONA */}
        <section id="regalos" className="py-24 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <div className="inline-block bg-rose-100 text-rose-600 px-4 py-2 rounded-full text-sm font-bold mb-6">
                ¿POR QUÉ ESTE REGALO?
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6 leading-tight">
                Por qué este regalo
                <span className="italic font-light text-rose-500"> sí </span>
                le va a gustar
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {reasons.map((reason, idx) => (
                <div 
                  key={idx} 
                  data-testid={`reason-card-${idx}`}
                  className="group bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border border-rose-100"
                >
                  <div className="bg-white w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-sm group-hover:scale-110 transition-transform">
                    <reason.icon className="text-rose-500" size={26} strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{reason.title}</h3>
                  <p className="text-gray-600 leading-relaxed font-light">{reason.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CÓMO LLEGA TU REGALO */}
        <section id="entrega" className="py-24 bg-gradient-to-br from-rose-50/40 to-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative order-2 lg:order-1">
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-rose-200/40 rounded-full blur-2xl"></div>
                <img 
                  src="/lifestyle/mujer_regalando.png"
                  alt="Mujer entregando regalo de Gafas La Tri"
                  className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[4/5]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block bg-rose-100 text-rose-600 px-4 py-2 rounded-full text-sm font-bold mb-6">
                  PRESENTACIÓN PREMIUM
                </div>
                <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6 leading-tight">
                  Así se ve
                  <span className="block italic font-light text-rose-500">cuando lo recibe</span>
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed font-light">
                  Tu pedido llega con caja y funda oficial de La Tri, listo para entregar tal como viene.
                </p>
                <div className="space-y-4">
                  {[
                    "Caja oficial de la Federación Ecuatoriana de Fútbol",
                    "Funda de microfibra incluida",
                    "Paño de limpieza premium",
                    "Lista para regalar sin envolver nada"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="bg-rose-100 p-1.5 rounded-full flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="text-rose-500" size={16} strokeWidth={2.5} />
                      </div>
                      <span className="text-gray-700 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST - Las grandes tiendas (con copy adaptado) */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-600 px-4 py-2 rounded-full text-sm font-bold mb-6">
                <Star size={16} fill="currentColor" />
                CALIDAD CERTIFICADA
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6 leading-tight">
                Un regalo que ya está en
                <span className="block italic font-light text-rose-500">las mejores vitrinas</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4 font-light max-w-3xl mx-auto">
                Las Gafas La Tri se venden en puntos de venta seleccionados de Supermaxi, Megamaxi, AKÍ, Marathon Sports e islas Panini. No es un producto improvisado: es el mismo que verías en esas cadenas, con la ventaja de pedirlo desde tu casa.
              </p>
              <p className="text-gray-600 italic font-light">
                Eso te asegura calidad, respaldo y que él reciba un regalo realmente oficial.
              </p>
            </div>
          </div>
          <Trust hideHeader={true} />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ParaMama;
